const API_BASE_URL =
  process.env.NEXT_PUBLIC_API_BASE_URL || "";

function getToken() {
  if (typeof window === "undefined") {
    return null;
  }

  return localStorage.getItem("token");
}

function getAuthHeaders() {
  const token = getToken();

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };

  if (token) {
    headers["Authorization"] = "Bearer " + token;
  }

  return headers;
}

function getUploadHeaders() {
  const token = getToken();

  const headers: Record<string, string> = {};

  if (token) {
    headers["Authorization"] = "Bearer " + token;
  }

  return headers;
}

async function handleResponse(response: Response) {
  if (response.status === 401) {
    if (typeof window !== "undefined") {
      localStorage.removeItem("token");
      localStorage.removeItem("role");
      window.location.href = "/login";
    }

    throw new Error("Unauthorized");
  }

  return response;
}

export async function login(username: string, password: string) {
  const response = await fetch(API_BASE_URL + "/auth/login", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      username,
      password,
    }),
  });

  await handleResponse(response);

  if (!response.ok) {
    throw new Error("Login failed");
  }

  return response.json();
}

export async function askQuestion(question: string) {
  const response = await fetch(API_BASE_URL + "/ask", {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify({
      question,
    }),
  });

  await handleResponse(response);

  if (!response.ok) {
    throw new Error("API request failed");
  }

  return response.json();
}

export async function getAdminDocuments() {
  const response = await fetch(API_BASE_URL + "/admin/documents", {
    headers: getAuthHeaders(),
  });

  await handleResponse(response);

  if (!response.ok) {
    throw new Error("Documents request failed");
  }

  return response.json();
}

export async function deleteAdminDocument(filename: string) {
  const response = await fetch(
    API_BASE_URL + "/admin/documents/" + encodeURIComponent(filename),
    {
      method: "DELETE",
      headers: getAuthHeaders(),
    }
  );

  await handleResponse(response);

  if (!response.ok) {
    throw new Error("Delete request failed");
  }

  return response.json();
}

export async function uploadDocument(file: File) {
  const formData = new FormData();

  formData.append("file", file);

  const response = await fetch(API_BASE_URL + "/admin/upload", {
    method: "POST",
    headers: getUploadHeaders(),
    body: formData,
  });

  await handleResponse(response);

  if (!response.ok) {
    throw new Error("Upload failed");
  }

  return response.json();
}

export async function getAdminStats() {
  const response = await fetch(API_BASE_URL + "/admin/stats", {
    headers: getAuthHeaders(),
  });

  await handleResponse(response);

  if (!response.ok) {
    throw new Error("Stats request failed");
  }

  return response.json();
}

export async function getAdminQueries(limit = 50) {
  const response = await fetch(
    API_BASE_URL + "/admin/queries?limit=" + limit,
    {
      headers: getAuthHeaders(),
    }
  );

  await handleResponse(response);

  if (!response.ok) {
    throw new Error("Queries request failed");
  }

  return response.json();
}

export async function getAdminUsers() {
  const response = await fetch(API_BASE_URL + "/admin/users", {
    headers: getAuthHeaders(),
  });

  await handleResponse(response);

  if (!response.ok) {
    throw new Error("Users request failed");
  }

  return response.json();
}

export async function createAdminUser(
  username: string,
  password: string,
  role: string
) {
  const response = await fetch(API_BASE_URL + "/admin/users", {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify({
      username,
      password,
      role,
    }),
  });

  await handleResponse(response);

  if (!response.ok) {
    throw new Error("Create user failed");
  }

  return response.json();
}

export async function setUserActive(userId: number, isActive: boolean) {
  const response = await fetch(API_BASE_URL + "/admin/users/" + userId + "/active", {
    method: "PATCH",
    headers: getAuthHeaders(),
    body: JSON.stringify({ is_active: isActive }),
  });

  await handleResponse(response);

  if (!response.ok) {
    throw new Error("Set user active failed");
  }

  return response.json();
}


export async function setUserRole(userId: number, role: string) {
  const response = await fetch(API_BASE_URL + "/admin/users/" + userId + "/role", {
    method: "PATCH",
    headers: getAuthHeaders(),
    body: JSON.stringify({ role }),
  });

  await handleResponse(response);

  if (!response.ok) {
    throw new Error("Set user role failed");
  }

  return response.json();
}


export async function resetUserPassword(userId: number, password: string) {
  const response = await fetch(API_BASE_URL + "/admin/users/" + userId + "/password", {
    method: "PATCH",
    headers: getAuthHeaders(),
    body: JSON.stringify({ password }),
  });

  await handleResponse(response);

  if (!response.ok) {
    throw new Error("Reset password failed");
  }

  return response.json();
}
